// admin-ui/src/components/ReplyActionCards.tsx
/** Proposed account actions on a draft: preflight, edit params, skip, staff verification. */
import { useState } from 'react'
import { api } from '../api/client'
import { ACTION_TITLES, EDITABLE_PARAMS, MEMBERSHIP_CHOICES, type ActionChoice, type ReplyActionView } from '../lib/replyActions'

type Props = {
  actions: ReplyActionView[]
  choices: Record<number, ActionChoice>
  onChange: (index: number, next: ActionChoice, expectedText?: string) => void
}

const VERDICT_TEXT = {
  can_apply: 'Will apply now', needs_confirmation: 'Will send a confirmation link',
  needs_verification: 'Needs you to verify the member', refused: 'Refused',
}
const BILLING = new Set(['refund_charge', 'stop_auto_renew'])

export function ReplyActionCards({ actions, choices, onChange }: Props) {
  const [editing, setEditing] = useState<number | null>(null)
  const [draftParams, setDraftParams] = useState<Record<string, string>>({})
  const [checks, setChecks] = useState<Record<number, { verdict: string; reason: string }>>({})
  const visible = actions.filter((a) => a.status !== 'superseded' && a.status !== 'refused')
  if (visible.length === 0) return null

  async function recheck(a: ReplyActionView, params: Record<string, string>, staffVerified: boolean) {
    const r = await api.post<{ verdict: string; reason: string; expected_text: string }>(
      `/api/replies/actions/${a.id}/check`, { params, staff_verified: staffVerified })
    setChecks((c) => ({ ...c, [a.index]: r }))
    return r
  }

  return (
    <div className="space-y-2">
      <p className="label">Account actions</p>
      {visible.map((a) => {
        const choice = choices[a.index] ?? { index: a.index, skip: false, staff_verified: false }
        const check = checks[a.index] ?? { verdict: a.preflight.verdict ?? '', reason: a.preflight.reason ?? '' }
        const done = a.status === 'applied' || a.status === 'confirmation_sent' || a.status === 'confirmed'
        return (
          <div key={a.id} className={`border p-2 text-sm ${choice.skip ? 'border-ink-600 opacity-60' : 'border-amber-glow'}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-paper font-semibold">{a.index}. {ACTION_TITLES[a.type]}</span>
              {a.type === 'change_email' && <span className="text-paper-dim">→ {a.params.new_email}</span>}
              {a.type === 'update_name' && <span className="text-paper-dim">→ {a.params.first_name} {a.params.last_name}</span>}
              {a.type === 'refund_charge' && <span className="text-paper-dim">→ payment {a.params.order_ref}</span>}
              <span className="label">{done ? a.status.replace('_', ' ')
                : a.status === 'applying' ? "Started but didn't finish: check Site Orders, then skip"
                : VERDICT_TEXT[check.verdict as keyof typeof VERDICT_TEXT] ?? ''}</span>
            </div>
            <p className="text-paper-trace text-xs">{check.reason}{a.error ? ` · Last error: ${a.error}` : ''}</p>
            {!done && (
              <div className="flex flex-wrap gap-3 mt-1">
                <button type="button" className="label hover:underline" onClick={() => {
                  onChange(a.index, { ...choice, skip: !choice.skip })
                }}>{choice.skip ? 'Keep' : 'Skip'}</button>
                {EDITABLE_PARAMS[a.type].length > 0 && (
                  <button type="button" className="label hover:underline" onClick={() => {
                    setEditing(a.index); setDraftParams({ ...a.params, ...(choice.params ?? {}) })
                  }}>Edit</button>
                )}
                {(a.type === 'change_email' || BILLING.has(a.type)) && (
                  <label className="label flex items-center gap-1">
                    <input id={`verified-${a.id}`} type="checkbox" checked={choice.staff_verified} onChange={async (e) => {
                      const next = { ...choice, staff_verified: e.target.checked }
                      const r = await recheck(a, { ...a.params, ...(choice.params ?? {}) }, e.target.checked)
                      onChange(a.index, next, r.expected_text)
                    }} />
                    {BILLING.has(a.type) ? "Verified it's the member (account number and ZIP, or a call)" : 'Verified by account number and ZIP'}
                  </label>
                )}
              </div>
            )}
            {editing === a.index && (
              <div className="mt-2 space-y-1">
                {EDITABLE_PARAMS[a.type].map((k) => (
                  <label key={k} className="block">
                    <span className="label">{k === 'amount' ? 'amount (blank = all still refundable)' : k.replace('_', ' ')}</span>
                    {k === 'membership' ? (
                      <select id={`p-${a.id}-${k}`} value={draftParams[k] ?? ''}
                        onChange={(e) => setDraftParams((p) => ({ ...p, [k]: e.target.value }))}
                        className="ml-2 bg-ink-900 border border-ink-600 text-paper text-[13px] px-2 py-1">
                        {MEMBERSHIP_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    ) : (
                      <input id={`p-${a.id}-${k}`} value={draftParams[k] ?? ''}
                        onChange={(e) => setDraftParams((p) => ({ ...p, [k]: e.target.value }))}
                        className="ml-2 bg-ink-900 border border-ink-600 text-paper text-[13px] px-2 py-1" />
                    )}
                  </label>
                ))}
                <button type="button" className="label-amber hover:underline" onClick={async () => {
                  const r = await recheck(a, draftParams, choice.staff_verified)
                  onChange(a.index, { ...choice, params: draftParams }, r.expected_text)
                  setEditing(null)
                }}>Check and keep</button>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
